import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import api from '../../utils/api';

const NotificationBell = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnread(res.data?.data?.count ?? 0);
    } catch {
      // silent — never crash the header
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setItems(res.data?.data ?? []);
    } catch {
      // silent — never crash the header
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  };

  const handleItemClick = async (item) => {
    if (!item.read) {
      setItems((prev) => prev.map((n) => (n._id === item._id ? { ...n, read: true } : n)));
      setUnread((prev) => Math.max(0, prev - 1));
      try {
        await api.patch(`/notifications/${item._id}/read`);
      } catch {
        // silent
      }
    }
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  const handleMarkAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await api.patch('/notifications/read-all');
    } catch {
      // silent
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-lg text-zip-muted hover:text-zip-text hover:bg-zip-hover transition-all duration-150 focus-ring"
        aria-label="Notifications"
      >
        <Bell size={17} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-[3px] rounded-full bg-zip-red text-white text-[9px] font-bold font-mono flex items-center justify-center leading-none">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-80 bg-zip-surface border border-zip-border rounded-xl shadow-lg overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zip-border">
            <span className="text-sm font-semibold text-zip-text">Notifications</span>
            <button
              onClick={handleMarkAllRead}
              className="text-[11px] font-mono text-zip-red hover:text-zip-red/80 transition-colors"
            >
              Mark all read
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading && items.length === 0 && (
              <p className="px-4 py-6 text-xs text-zip-muted text-center">Loading...</p>
            )}
            {!loading && items.length === 0 && (
              <p className="px-4 py-6 text-xs text-zip-muted text-center">No notifications yet</p>
            )}
            {items.map((item) => (
              <button
                key={item._id}
                onClick={() => handleItemClick(item)}
                className={`w-full flex items-start gap-2.5 px-4 py-3 text-left hover:bg-zip-hover transition-colors border-l-2 ${
                  item.read ? 'border-transparent' : 'border-zip-red'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${item.read ? 'text-zip-muted font-normal' : 'text-zip-text font-medium'}`}>
                    {item.title}
                  </div>
                  <div className="text-xs text-zip-muted truncate mt-0.5">{item.message}</div>
                  <div className="text-[10px] font-mono text-zip-muted mt-1">
                    {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                  </div>
                </div>
                {!item.read && <span className="w-1.5 h-1.5 rounded-full bg-zip-red flex-shrink-0 mt-1.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
